import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// The generated Supabase types have drifted from the live schema, so the partner
// tables are reached through `as any` casts — the same approach the admin pages use.
const db = supabase as any;

export type PartnerStatus =
  | "draft"
  | "pending_review"
  | "more_info_requested"
  | "active"
  | "rejected"
  | "suspended";

export interface PartnerRecord {
  id: string;
  owner_user_id: string;
  company_name: string;
  website: string | null;
  country: string | null;
  phone: string | null;
  address: string | null;
  registration_no: string | null;
  qualification: Record<string, unknown>;
  status: PartnerStatus;
  tier: string | null;
  valid_until: string | null;
  review_notes: string | null;
  created_at: string;
}

/** A partner is only usable when active AND not past valid_until (mirrors is_active_partner() in SQL). */
export function isPartnerUsable(partner: PartnerRecord | null | undefined): boolean {
  if (!partner || partner.status !== "active") return false;
  if (!partner.valid_until) return true;
  // valid_until is an inclusive date; compare on calendar days, not timestamps.
  return partner.valid_until >= new Date().toISOString().slice(0, 10);
}

/** The partners row owned by the signed-in user, or null if they never applied. */
export function usePartnerRecord() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["partner", "self", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<PartnerRecord | null> => {
      const { data, error } = await db
        .from("partners")
        .select("*")
        .eq("owner_user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useOpenDocumentRequests(partnerId: string | undefined) {
  return useQuery({
    queryKey: ["partner", "doc-requests", partnerId],
    enabled: !!partnerId,
    queryFn: async () => {
      const { data, error } = await db
        .from("partner_document_requests")
        .select("*")
        .eq("partner_id", partnerId)
        .eq("status", "open")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface PartnerApplicationRow {
  id: string;
  status: string;
  membership_package: string;
  created_at: string;
  submitted_at: string | null;
  user_id: string;
  clientName: string;
  clientEmail: string;
}

/**
 * Applications belonging to this partner's clients. RLS ("Partners read client
 * applications") is what actually scopes the rows — the partner_id filter is
 * only a narrowing hint, never the security boundary.
 */
export function usePartnerApplications(opts: { search?: string; status?: string } = {}) {
  const { data: partner } = usePartnerRecord();
  const search = (opts.search ?? "").trim().toLowerCase();
  const status = opts.status ?? "all";

  return useQuery({
    queryKey: ["partner", "applications", partner?.id, status],
    enabled: !!partner?.id,
    queryFn: async (): Promise<PartnerApplicationRow[]> => {
      let query = db
        .from("applications")
        .select("id, status, membership_package, created_at, submitted_at, user_id")
        .eq("partner_id", partner!.id)
        .order("created_at", { ascending: false });
      if (status !== "all") query = query.eq("status", status);

      const { data: apps, error } = await query;
      if (error) throw error;
      if (!apps?.length) return [];

      const userIds = Array.from(new Set(apps.map((a: any) => a.user_id)));
      const { data: profiles } = await db
        .from("profiles")
        .select("id, given_name, surname, email")
        .in("id", userIds);
      const byId = new Map((profiles ?? []).map((p: any) => [p.id, p]));

      return apps.map((a: any) => {
        const p: any = byId.get(a.user_id);
        return {
          ...a,
          clientName: [p?.given_name, p?.surname].filter(Boolean).join(" ") || "—",
          clientEmail: p?.email ?? "—",
        };
      });
    },
    // Filtering by name/email is done client-side: the set is one partner's book
    // of clients, small enough that a server round-trip per keystroke is waste.
    select: (rows: PartnerApplicationRow[]) =>
      search
        ? rows.filter(
            (r) =>
              r.clientName.toLowerCase().includes(search) ||
              r.clientEmail.toLowerCase().includes(search),
          )
        : rows,
  });
}

/** Creates the client auth user + a draft application, via the service-role edge function. */
export function useCreateClientApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
      membershipPackage?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("partner-create-client", {
        body: input,
      });
      if (error) {
        // The function returns a structured 409 when the email already has an account.
        const context = (error as any).context;
        const detail = typeof context?.body === "string" ? context.body : "";
        if (detail.includes("CLIENT_EXISTS")) {
          throw new Error("An account already exists for this email. Contact support to link it.");
        }
        throw error;
      }
      if (data?.error) throw new Error(data.error);
      return data as { clientUserId: string; applicationId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner", "applications"] });
    },
  });
}
